// Segment rows for the "splits" panel (2026-10-11). One normalized row shape for the Garmin sources
// (Strava laps removed, C2 2026-10-21):
//   Garmin watch laps    (/garmin/activities/{id}/laps)     — time = timer time (= Garmin Connect "Time")
//   Garmin per-km splits (/garmin/activities/{id}/splits)
// A segment type (work / rest …) is shown ONLY when the source states it for certain (Garmin structured
// workout laps); per-km splits never carry one. Nothing here is guessed or recomputed beyond formatting.

export const KIND_LABEL = { work: 'עבודה', rest: 'מנוחה', recovery: 'התאוששות', warmup: 'חימום', cooldown: 'שחרור' };
const RESTING = new Set(['rest', 'recovery']);

const num = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : (v != null && v !== '' && Number.isFinite(Number(v)) ? Number(v) : null));

export function paceToSec(p) {
  if (!p) return null;
  const parts = String(p).split(':').map(Number);
  if (parts.length !== 2 || !parts.every(Number.isFinite)) return null;
  return parts[0] * 60 + parts[1];
}

/** 90 -> "1:30", 3725 -> "1:02:05"; null when unknown (never "0:00" for a missing value). */
export function formatSegmentTime(sec) {
  const s = num(sec);
  if (s == null || s < 0) return null;
  const t = Math.round(s);
  const h = Math.floor(t / 3600), m = Math.floor((t % 3600) / 60), r = t % 60;
  const pad = (x) => String(x).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(r)}` : `${m}:${pad(r)}`;
}

/** Garmin Connect shows km with 2 decimals; under 1 km we show metres (0.19 -> "190 מ׳"). */
export function formatSegmentDistance(km) {
  const d = num(km);
  if (d == null) return null;
  return d < 1 ? `${Math.round(d * 1000)} מ׳` : d.toFixed(2);
}

const row = ({ n, distanceKm, timeSec, pace, paceSec, hr, kind }) => {
  const resting = RESTING.has(kind);
  return {
    n,
    kind: kind && KIND_LABEL[kind] ? kind : null,
    distance: formatSegmentDistance(distanceKm),
    time: formatSegmentTime(timeSec),
    // Pace of a rest segment is meaningless — shown as "—"; never feeds the fastest marker.
    pace: resting ? null : (pace || null),
    paceSec: resting ? null : (num(paceSec) ?? paceToSec(pace)),
    hr: num(hr) != null ? Math.round(num(hr)) : null,
  };
};

export function garminLapRows(view) {
  const laps = view && Array.isArray(view.laps) ? view.laps : [];
  return laps.map((l, i) => row({
    n: l.lap ?? i + 1, distanceKm: l.distance_km, timeSec: l.timer_s, pace: l.pace, paceSec: l.pace_sec,
    hr: l.avg_hr, kind: view.structured ? l.kind : null,
  }));
}

export function garminKmRows(splits) {
  return (Array.isArray(splits) ? splits : []).map((s, i) => row({
    n: s.split ?? i + 1, distanceKm: s.distance_km, timeSec: s.timer_s, pace: s.pace, paceSec: s.pace_sec,
    hr: s.avg_hr, kind: null,
  }));
}

/** Index of the fastest row (lowest pace) — among work segments when any are marked, else among all. */
export function fastestIndex(rows) {
  const hasWork = rows.some((r) => r.kind === 'work');
  let best = -1;
  rows.forEach((r, i) => {
    if (r.paceSec == null || (hasWork && r.kind !== 'work')) return;
    if (best < 0 || r.paceSec < rows[best].paceSec) best = i;
  });
  return best;
}
